/**
 * `/v1/ops/codex-capacity-alerts/*` transport (#2575).
 *
 * Raw calls only — React Query wiring lives in `useCodexCapacityAlerts.ts`,
 * matching the `accountBalanceAlerts.ts` / `useAccountBalanceAlerts.ts` split.
 *
 * Note what is NOT here: no client-side copy of the switch's default, the
 * fire/recover thresholds, the suppression semantics, or the preview
 * criteria. All of those are the platform route's, and a second statement
 * of them here would be the one that drifts. In particular the console
 * never assumes "no settings row means on" — the absence of the row is
 * read as `false` server-side, and reading it any other way in the
 * browser would re-create the failure the shipped-closed default exists
 * to prevent.
 */
import type {
  CodexCapacityAlertHistoryResponse,
  CodexCapacityAlertIncidentsResponse,
  CodexCapacityAlertPreviewResponse,
  CodexCapacityAlertSettingsResponse,
  CodexCapacityAlertStateResponse,
  CodexCapacityAlertSuppressionsResponse,
  CodexCapacityAlertTestSendRequest,
  CodexCapacityAlertTestSendResponse,
  CodexCapacityExcludedSkusResponse,
  CreateCodexCapacityAlertSuppressionRequest,
  CreateCodexCapacityAlertSuppressionResponse,
  CreateCodexCapacityExcludedSkuRequest,
  CreateCodexCapacityExcludedSkuResponse,
  DeleteCodexCapacityAlertSuppressionResponse,
  DeleteCodexCapacityExcludedSkuRequest,
  DeleteCodexCapacityExcludedSkuResponse,
  UpdateCodexCapacityAlertSettingsRequest,
} from '@nadoutong/contracts';
import { CODEX_CAPACITY_ALERT_HISTORY_DEFAULT_LIMIT } from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/codex-capacity-alerts';

/**
 * How many history / incident rows one read asks for.
 *
 * Sent EXPLICITLY rather than left to the route's own default, even though
 * the two currently agree. The console has to tell an operator whether what
 * they are looking at is the whole record, and the only signal available
 * for that — the response carries no `has_more` — is "did I get back as
 * many rows as I asked for". That comparison is only sound if the console
 * knows the number, so it states it instead of inferring it.
 */
export const CODEX_CAPACITY_ALERT_HISTORY_LIMIT = CODEX_CAPACITY_ALERT_HISTORY_DEFAULT_LIMIT;

function suppressionPath(accountId: string): string {
  return `${BASE}/suppressions/${encodeURIComponent(accountId)}`;
}

function historyPath(incidentId: string | null): string {
  const params = new URLSearchParams();
  params.set('limit', String(CODEX_CAPACITY_ALERT_HISTORY_LIMIT));
  if (incidentId !== null && incidentId.length > 0) {
    params.set('incident_id', incidentId);
  }
  return `${BASE}/history?${params.toString()}`;
}

export async function getCodexCapacityAlertSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CodexCapacityAlertSettingsResponse> {
  return opsApiRequest<CodexCapacityAlertSettingsResponse>(
    `${BASE}/settings`,
    { signal },
    fetchImpl,
  );
}

export async function updateCodexCapacityAlertSettings(
  body: UpdateCodexCapacityAlertSettingsRequest,
  fetchImpl: typeof fetch,
): Promise<CodexCapacityAlertSettingsResponse> {
  return opsApiRequest<CodexCapacityAlertSettingsResponse>(
    `${BASE}/settings`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function getCodexCapacityAlertState(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CodexCapacityAlertStateResponse> {
  return opsApiRequest<CodexCapacityAlertStateResponse>(`${BASE}/state`, { signal }, fetchImpl);
}

export async function getCodexCapacityAlertPreview(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CodexCapacityAlertPreviewResponse> {
  return opsApiRequest<CodexCapacityAlertPreviewResponse>(
    `${BASE}/preview`,
    { signal },
    fetchImpl,
  );
}

export async function listCodexCapacityAlertIncidents(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CodexCapacityAlertIncidentsResponse> {
  return opsApiRequest<CodexCapacityAlertIncidentsResponse>(
    `${BASE}/incidents`,
    { signal },
    fetchImpl,
  );
}

export async function listCodexCapacityAlertHistory(
  fetchImpl: typeof fetch,
  incidentId: string | null,
  signal?: AbortSignal,
): Promise<CodexCapacityAlertHistoryResponse> {
  return opsApiRequest<CodexCapacityAlertHistoryResponse>(
    historyPath(incidentId),
    { signal },
    fetchImpl,
  );
}

export async function listCodexCapacityAlertSuppressions(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CodexCapacityAlertSuppressionsResponse> {
  return opsApiRequest<CodexCapacityAlertSuppressionsResponse>(
    `${BASE}/suppressions`,
    { signal },
    fetchImpl,
  );
}

export async function createCodexCapacityAlertSuppression(
  body: CreateCodexCapacityAlertSuppressionRequest,
  fetchImpl: typeof fetch,
): Promise<CreateCodexCapacityAlertSuppressionResponse> {
  return opsApiRequest<CreateCodexCapacityAlertSuppressionResponse>(
    `${BASE}/suppressions`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function deleteCodexCapacityAlertSuppression(
  accountId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteCodexCapacityAlertSuppressionResponse> {
  return opsApiRequest<DeleteCodexCapacityAlertSuppressionResponse>(
    suppressionPath(accountId),
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listCodexCapacityExcludedSkus(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<CodexCapacityExcludedSkusResponse> {
  return opsApiRequest<CodexCapacityExcludedSkusResponse>(
    `${BASE}/excluded-skus`,
    { signal },
    fetchImpl,
  );
}

export async function createCodexCapacityExcludedSku(
  body: CreateCodexCapacityExcludedSkuRequest,
  fetchImpl: typeof fetch,
): Promise<CreateCodexCapacityExcludedSkuResponse> {
  return opsApiRequest<CreateCodexCapacityExcludedSkuResponse>(
    `${BASE}/excluded-skus`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function deleteCodexCapacityExcludedSku(
  body: DeleteCodexCapacityExcludedSkuRequest,
  fetchImpl: typeof fetch,
): Promise<DeleteCodexCapacityExcludedSkuResponse> {
  return opsApiRequest<DeleteCodexCapacityExcludedSkuResponse>(
    `${BASE}/excluded-skus`,
    { method: 'DELETE', body },
    fetchImpl,
  );
}

export async function sendCodexCapacityAlertTest(
  body: CodexCapacityAlertTestSendRequest,
  fetchImpl: typeof fetch,
): Promise<CodexCapacityAlertTestSendResponse> {
  return opsApiRequest<CodexCapacityAlertTestSendResponse>(
    `${BASE}/test-send`,
    { method: 'POST', body },
    fetchImpl,
  );
}
